import React, { useState, useEffect, createContext } from 'react'
import {
  getEntityWithChildrenPaginatedQuery,
  getEntityReadersPaginated,
  getReader
} from 'api/queries'
import type { Entity, GetReader } from 'api/data.types'
import type { ItemProps } from 'components/admin/breadcrumb/Breadcrumb'
import { getQueryVariable } from 'utils/url'
import scrollTo from 'utils/scrollTo'
import { USERS_TABLE_WRAPPER_ID } from 'components/admin/entity-users-management/UserTable/UsersTable'

export type EntityUsersContextType = {
  currentEntity: Entity
  usersFromCurrentEntity: Entity['readersPerPage']
  breadcrumb: Array<ItemProps>
  entitiesPaginationCount?: number
  readersPaginationCount?: number
  requestedUser?: GetReader
}

export type EntityUsersDispatchContextType = {
  loadNextPage: () => void
  loadPrevPage: () => void
  requestEntity: (id: number) => void
  requestReader: (id: number) => void
  loadNextReadersPage?: () => void
  loadPrevReadersPage?: () => void
}

const EntityUsersContext = createContext<
  EntityUsersContextType | Record<string, never>
>({})
const EntityUsersDispatchContext = createContext<
  EntityUsersDispatchContextType | Record<string, never>
>({})

const errHandler = err =>
  global.alert(`Oops, aconteceu um erro: ${err.message}`)

function EntityUsersProvider({ children, client, user, history }) {
  const eidFromUrl = Number(getQueryVariable('eid'))
  const ePageFromUrl = Number(getQueryVariable('epage'))
  const [currentEntity, setCurrentEntity] = useState<Entity>({
    id: eidFromUrl
  })
  const [usersFromCurrentEntity, setUsersFromCurrentEntity] = useState(null)
  const [breadcrumb, setBreadCrumb] = useState([])
  const [entitiesPaginationCount, setEntitiesPaginationCount] = useState(
    ePageFromUrl ? ePageFromUrl : 1
  )
  const [readersPaginationCount, setReadersPaginationCount] = useState(1)

  const [requestedUser, setRequestedUser] = useState(null)

  const getEntityWithChildrenPaginated = (id: number, page: number): Entity => {
    return client
      .query({
        query: getEntityWithChildrenPaginatedQuery,
        variables: {
          id: id,
          page: page,
          perPage: 20
        }
      })
      .then(res => {
        const entity = res.data.getEntity

        return entity
      })
      .catch(errHandler)
  }

  const getReaderData = (id: number) => {
    client
      .query({
        query: getReader,
        variables: {
          id: id
        }
      })
      .then(({ data }) => {
        setRequestedUser({
          ...data.getReader
        })

        setTimeout(() => {
          scrollTo(
            document.documentElement,
            document
              .getElementById(USERS_TABLE_WRAPPER_ID)
              .getBoundingClientRect().top,
            300
          )
        }, 0)
      })
      .catch(errHandler)
  }

  useEffect(() => {
    async function loadData() {
      const entity = await getEntityWithChildrenPaginated(
        currentEntity.id,
        entitiesPaginationCount
      )

      const breadcrumb = entity.ancestryDetails
        ? entity.ancestryDetails.map(ancestry => ({
            id: ancestry.id,
            text: ancestry.name,
            url: `${history.location.pathname}?eid=${ancestry.id}&epage=1`
          }))
        : []

      breadcrumb.push({
        id: entity.id,
        text: entity.name,
        url: `${history.location.pathname}?eid=${entity.id}&epage=1`
      })

      setBreadCrumb(breadcrumb)
      setCurrentEntity(entity)
    }

    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEntity.id, entitiesPaginationCount])

  useEffect(() => {
    async function loadReaders() {
      setRequestedUser(null)
      if (currentEntity.id) {
        const allReaders = await client
          .query({
            query: getEntityReadersPaginated,
            variables: {
              id: currentEntity.id,
              page: readersPaginationCount,
              perPage: 20
            }
          })
          .then(({ data }) => data.getEntity.readersPerPage)
          .catch(errHandler)

        setUsersFromCurrentEntity(allReaders)
      }
    }

    loadReaders()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEntity.id, readersPaginationCount])

  useEffect(() => {
    if (eidFromUrl && eidFromUrl !== currentEntity.id) {
      setCurrentEntity({ id: eidFromUrl })
      setEntitiesPaginationCount(1)
      setReadersPaginationCount(1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eidFromUrl])

  useEffect(() => {
    if (ePageFromUrl && ePageFromUrl !== entitiesPaginationCount) {
      setEntitiesPaginationCount(ePageFromUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ePageFromUrl])

  return (
    <EntityUsersContext.Provider
      value={{
        currentEntity,
        usersFromCurrentEntity,
        breadcrumb,
        entitiesPaginationCount,
        requestedUser,
        readersPaginationCount
      }}
    >
      <EntityUsersDispatchContext.Provider
        value={{
          loadNextPage: () => {
            const page = entitiesPaginationCount + 1
            history.push({
              ...history.location,
              search: `eid=${currentEntity.id}&epage=${page}`
            })
          },
          loadPrevPage: () => {
            const page = entitiesPaginationCount - 1
            history.push({
              ...history.location,
              search: `eid=${currentEntity.id}&epage=${page}`
            })
          },
          requestEntity: id => {
            history.push({
              ...history.location,
              search: `eid=${id}&epage=1`
            })
          },
          requestReader: id => {
            getReaderData(id)
          },
          loadNextReadersPage: () => {
            const page = readersPaginationCount + 1
            setReadersPaginationCount(page)
          },
          loadPrevReadersPage: () => {
            const page = readersPaginationCount - 1
            setReadersPaginationCount(page)
          }
        }}
      >
        {children}
      </EntityUsersDispatchContext.Provider>
    </EntityUsersContext.Provider>
  )
}

export { EntityUsersProvider, EntityUsersContext, EntityUsersDispatchContext }
